import { useParams } from 'react-router-dom'

import { UserAvatar } from '@/shared/components/common'
import { ErrorState, SkeletonRows } from '@/shared/components/data'
import { Markdown } from '@/shared/components/ui'
import { useI18n } from '@/shared/i18n'
import { countOrDash } from '@/shared/lib/format'

import { useCompany, useCompanyContacts } from '../api'

function Metric({
  label,
  value,
  alert,
}: {
  label: string
  /** `null` = el backend no lo informa. Se dibuja un guión, no un cero. */
  value: number | null
  alert?: boolean
}) {
  return (
    <div className="hairline flex flex-col gap-1 rounded-[var(--radius-card)] bg-surface-1 p-3">
      <span className="text-2xs text-text-2">{label}</span>
      <span className={`num text-lg leading-none ${alert ? 'text-overdue' : 'text-text-1'}`}>
        {alert ? <span aria-hidden="true">! </span> : null}
        {countOrDash(value)}
      </span>
    </div>
  )
}

export function CompanySummaryTab() {
  const { t, formatDate } = useI18n()
  const { companyId = '' } = useParams()
  const query = useCompany(companyId)
  const contacts = useCompanyContacts(companyId)

  if (query.isPending) return <SkeletonRows rows={4} />
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  const company = query.data
  const primary = (contacts.data ?? []).find((contact) => contact.is_primary) ?? null

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label={t('companies.metric.activeProjects')} value={company.active_projects} />
        <Metric label={t('companies.metric.openTasks')} value={company.open_tasks} />
        <Metric
          label={t('companies.metric.overdueTasks')}
          value={company.overdue_tasks}
          alert={(company.overdue_tasks ?? 0) > 0}
        />
        <Metric label={t('companies.metric.contacts')} value={company.contacts_count} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px]">
        <section className="flex flex-col gap-2">
          <h2 className="text-base text-text-1">{t('companies.summary.internalNotes')}</h2>
          <Markdown empty={t('companies.summary.noInternalNotes')}>
            {company.notes_internal}
          </Markdown>
        </section>

        <aside className="hairline flex flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-3">
          <div className="flex flex-col gap-1">
            <span className="text-2xs tracking-wide text-text-3 uppercase">
              {t('companies.summary.techOwner')}
            </span>
            <UserAvatar user={company.primary_tech_owner} withName />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-2xs tracking-wide text-text-3 uppercase">
              {t('companies.summary.primaryContact')}
            </span>
            {primary ? (
              <>
                <span className="text-xs text-text-1">{primary.name}</span>
                <span className="num text-2xs text-text-3">{primary.email}</span>
              </>
            ) : (
              <span className="text-xs text-text-3">{t('companies.summary.noPrimaryContact')}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-2xs tracking-wide text-text-3 uppercase">
              {t('companies.summary.slackChannel')}
            </span>
            <span className="num text-xs text-text-2">
              {company.slack_channel ?? t('companies.summary.noSlackChannelLong')}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-2xs tracking-wide text-text-3 uppercase">
              {t('companies.summary.clientSince')}
            </span>
            <span className="num text-xs text-text-2">{formatDate(company.created_at)}</span>
          </div>
        </aside>
      </div>
    </div>
  )
}
